import {ActionIcon, Tooltip} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React from 'react';

import isArchived from 'archive/isArchived';
import ChemicalStructureViewer from 'chemistry/molecules/ChemicalStructureViewer';
import {EditChemicalStructureModal} from 'chemistry/molecules/EditChemicalStructureModal';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runModal from 'ui-library/runModal';
import {CenteredSpinner} from 'ui-library/Spinner';

export const MOLECULE_CHEMICAL_STRUCTURE_LATEST_VERSION_QUERY = gql`
  query MoleculeChemicalStructureLatestVersionQuery(
    $moleculeId: StrictString!
    $substructureQuery: StrictString
    $shouldHighlight: StrictBoolean
  ) @skipAudit @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        archiveRecord {
          ...ArchiveRecordGraphQL
        }
        name
        molfile
        currentVersion {
          id
          imageData(referenceSubstructure: $substructureQuery, shouldHighlight: $shouldHighlight) {
            ... on SVGNode {
              base64Svg
            }
            ... on ErrorNode {
              message
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MOLECULE_CHEMICAL_STRUCTURE_BY_VERSION_QUERY = gql`
  query MoleculeChemicalStructureByVersionQuery(
    $moleculeId: StrictString!
    $moleculeVersionId: StrictString!
    $substructureQuery: StrictString
    $shouldHighlight: StrictBoolean
  ) @skipAudit @engComponent(name: CHEMISTRY) {
    molecule(id: $moleculeId) {
      ... on MoleculeNode {
        id
        archiveRecord {
          ...ArchiveRecordGraphQL
        }
        name
        molfile
        currentVersion {
          id
        }
        version(versionId: $moleculeVersionId) {
          id
          imageData(referenceSubstructure: $substructureQuery, shouldHighlight: $shouldHighlight) {
            ... on SVGNode {
              base64Svg
            }
            ... on ErrorNode {
              message
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ArchiveRecord').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export enum MoleculeChemicalStructureSize {
  FULL_SIZE = 'FULL_SIZE', // 100%x100%
  ITEM_LISTING_SIZE = 'ITEM_LISTING_SIZE', // 200x200
}

interface MoleculeChemicalStructureContentsProps {
  moleculeId: string;
  moleculeName: string;
  currentVersionId: string;
  selectedVersionId: string;
  molfile: string;
  imageData: {
    __typename: string;
    base64Svg?: string;
    message?: string;
  };
  size?: MoleculeChemicalStructureSize;
  isInaccessible: boolean;
  editable?: boolean;
  isArchived: boolean;
}

function MoleculeChemicalStructureContents(
  props: MoleculeChemicalStructureContentsProps
): JSX.Element | null {
  const [opened, {open, close}] = useDisclosure(false);
  if (props.isInaccessible) {
    return <div>You do not have access to view this molecule. Please ask your administrator for access.</div>;
  }
  const base64Svg = props.imageData.__typename === 'SVGNode' ? props.imageData.base64Svg : null;
  const errorMessage = props.imageData.__typename === 'ErrorNode' ? props.imageData.message : null;
  const imgClassName =
    props.size === MoleculeChemicalStructureSize.FULL_SIZE
      ? css(styles.maxHeightMaxWidth)
      : props.size === MoleculeChemicalStructureSize.ITEM_LISTING_SIZE
        ? css(styles.listingSize)
        : '';
  return (
    <div className={css(styles.panelContent)}>
      <div className={css(styles.header)}>
        {base64Svg != null && (
          <Tooltip label="Expand Chemical Structure">
            <ActionIcon
              aria-label="Expand Chemical Structure"
              variant="transparent"
              size="md"
              radius="md"
              onClick={open}
            >
              <BenchlingIcon icon="square-corners" />
            </ActionIcon>
          </Tooltip>
        )}
        {props.editable && (
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            icon="edit"
            size={ButtonSize.SMALL}
            text="Edit"
            onClick={() =>
              runModal(EditChemicalStructureModal, {
                moleculeId: props.moleculeId,
                expectedCurrentVersionId: props.currentVersionId,
                initialChemicalStructure: props.molfile,
              })
            }
            disabled={props.currentVersionId !== props.selectedVersionId || props.isArchived}
            dataTestAttrs={{component: 'MoleculeChemicalStructure', element: 'editButton'}}
          />
        )}
      </div>
      {base64Svg != null && (
        <>
          <img
            {...getTestProps({component: 'MoleculeChemicalStructure', element: 'chemicalStructure'})}
            className={imgClassName}
            src={`data:image/svg+xml;base64,${base64Svg}`}
          />
          <ChemicalStructureViewer
            title={`Chemical structure of ${props.moleculeName}`}
            base64Svg={base64Svg}
            opened={opened}
            onClose={close}
          />
        </>
      )}
      {errorMessage != null && <div>{errorMessage}</div>}
    </div>
  );
}

interface MoleculeChemicalStructureProps {
  moleculeId: string;
  moleculeVersionId?: string;
  editable?: boolean;
  size?: MoleculeChemicalStructureSize;
  substructureQuery?: string | null;
  shouldHighlightSubstructure?: boolean;
}

export default function MoleculeChemicalStructure(props: MoleculeChemicalStructureProps): JSX.Element | null {
  return (
    <BenchlingQuery
      query={
        props.moleculeVersionId
          ? MOLECULE_CHEMICAL_STRUCTURE_BY_VERSION_QUERY
          : MOLECULE_CHEMICAL_STRUCTURE_LATEST_VERSION_QUERY
      }
      variables={
        props.moleculeVersionId
          ? {
              moleculeId: props.moleculeId,
              moleculeVersionId: props.moleculeVersionId,
              substructureQuery: props.substructureQuery,
              shouldHighlight: props.shouldHighlightSubstructure,
            }
          : {
              moleculeId: props.moleculeId,
              substructureQuery: props.substructureQuery,
              shouldHighlight: props.shouldHighlightSubstructure,
            }
      }
      renderLoading={() => <CenteredSpinner />}
    >
      {({molecule}) => (
        <MoleculeChemicalStructureContents
          moleculeId={molecule.id}
          moleculeName={molecule.name}
          currentVersionId={molecule.currentVersion.id}
          selectedVersionId={props.moleculeVersionId ? props.moleculeVersionId : molecule.currentVersion.id}
          molfile={molecule.molfile}
          imageData={props.moleculeVersionId ? molecule.version.imageData : molecule.currentVersion.imageData}
          size={props.size}
          isInaccessible={isInaccessible(molecule)}
          editable={props.editable}
          isArchived={isArchived(molecule)}
        />
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  panelContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    width: '100%',
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  maxHeightMaxWidth: {
    height: '100%',
    width: '100%',
  },
  listingSize: {
    height: 200,
    width: 200,
  },
});
